import React, { FunctionComponent, memo } from "react";
import { Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Multiselect, MultiselectOption } from "@shared/components";
import { investorRequestSelector } from "@investorRequest/store/selectors";
import useMultiselectHandlers from "@shared/hooks/useMultiselectHandlers";
import { updateCommonRequestData } from "@investorRequest/store/ducks";
import { useAppSelector, useMobileLayout } from "@app/hooks";

export const useStyles = makeStyles(() => ({}));

interface Props {
  required?: boolean;
  funds: MultiselectOption[];
  accounts: MultiselectOption[];
  accountsDisabled?: boolean;
}

const InvestorRequestEntitiesDropdowns: FunctionComponent<Props> = ({
  funds,
  accounts,
  required,
  accountsDisabled,
}) => {
  const { commonFields, validationWarningShown } = useAppSelector(investorRequestSelector);
  const { selectedAccounts, selectedFunds } = useAppSelector((state) => ({
    selectedAccounts: state.investorRequest.commonFields.selectedAccounts,
    selectedFunds: state.investorRequest.commonFields.selectedFunds,
  }));

  const [handleSelectFund, handleClearAllFunds] = useMultiselectHandlers({
    entityName: "selectedFunds",
    nullIfEmpty: false,
    selectedValues: selectedFunds,
    action: updateCommonRequestData,
  });

  const [handleSelectAccount, handleClearAllAccounts] = useMultiselectHandlers({
    entityName: "selectedAccounts",
    nullIfEmpty: false,
    selectedValues: selectedAccounts,
    action: updateCommonRequestData,
  });

  useStyles();
  const isMobile = useMobileLayout();

  return (
    <Grid item xs={12}>
      <Grid
        item
        container
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 4 : 7}
        data-testid="investor-request-entities-dropdowns"
      >
        <Grid item xs={6}>
          <Multiselect
            entity="fund"
            size="large"
            variant="contained"
            options={funds}
            onSelect={handleSelectFund}
            onClearAll={handleClearAllFunds}
            withSearch
          />
          {required && validationWarningShown && !commonFields.selectedFunds.length ? (
            <div style={{ color: "#AB8433", fontSize: 12, marginTop: 4 }}>Please select at least one fund</div>
          ) : null}
        </Grid>
        <Grid item xs={6}>
          <Multiselect
            entity="account"
            size="large"
            variant="contained"
            options={accounts}
            onSelect={handleSelectAccount}
            onClearAll={handleClearAllAccounts}
            disabled={accountsDisabled}
            withSearch
          />
          {required && validationWarningShown && !accountsDisabled && !commonFields.selectedAccounts.length ? (
            <div style={{ color: "#AB8433", fontSize: 12, marginTop: 4 }}>Please select at least one account</div>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo<Props>(InvestorRequestEntitiesDropdowns);
